import { put, takeLatest, all, select, take, cps } from 'redux-saga/effects'
import { updates } from 'fn-update'
import { asynch} from 'redux-asynch-middleware'

import { Machine } from '../../goose'
import { loginWithMessenger } from '../../../api'
import { isMessengerReady } from '../messenger'
import AuthModule, { LOGIN_REQUESTED, registerAsLoginProvider, login } from './auth'

const APPID = process.env.REACT_APP_FACEBOOK_APP_ID

const NAMESPACE = 'messengerAuth'
const ns = (label) => `${NAMESPACE}/${label}`

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Action creators
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const INITIALISE = ns('INITIALISE')
const CONTEXT_LOADED = ns('CONTEXT_LOADED')
const LOGIN = asynch(ns('LOGIN'))

// This needs to get called by a container somewhere to kick things off.
export const initialise = () => ({ type: INITIALISE })

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Selectors
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const selectSignedRequest = (state) => {
  return state[NAMESPACE].signed_request
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Reducer
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const reducer = Machine({
  [Machine.INIT]: () => ({
    ready: false,
    thread_type: null,
    tid: null,
    psid: null,
    signed_request: null
  }),

  [CONTEXT_LOADED]: ({ context }) => updates({
    ready: true,
    ...context
  })
})

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Sagas
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

// We need to do this callback trickery because the Messenger SDK does not follow the
// conventional node.js CPS.
const loadContext = (appId, callback) => {
  const success = (context) => callback(null, context)
  const error = (err) => callback(err, null)
  window.MessengerExtensions.getContext(appId, success, error)
}

function* onInitialise() {
  let ready = yield select(isMessengerReady)

  // Block until the messenger module is ready. We shouldn't be calling this saga
  // if we're not in a webview anyway.
  while (!ready) {
    yield take()
    ready = yield select(isMessengerReady)
  }

  // Asynchronously load the context and dispatch an action. When this is loaded we
  // can proceed with authentication.
  const context = yield cps(loadContext, APPID)
  yield put({ type: CONTEXT_LOADED, payload: { context } })
}

function* onLoginRequested() {
  const signedRequest = yield select(selectSignedRequest)
  yield put({ type: LOGIN, promise: loginWithMessenger(signedRequest) })
}

function* onSuccessfulLogin(action) {
  const { user, api_token } = action.result
  // Tell the auth module to log us in!
  yield put(login({ user: user, api_token: api_token }))
}

function* onContextLoaded() {
  yield put(registerAsLoginProvider('messenger'))
  yield all([
    takeLatest(LOGIN_REQUESTED, onLoginRequested),
    takeLatest(LOGIN, onSuccessfulLogin),
  ])
}

const saga = function*() {
  yield all([
    takeLatest(INITIALISE, onInitialise),
    takeLatest(CONTEXT_LOADED, onContextLoaded)
  ])
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Module exports
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export default {
  key: 'messengerAuth',
  reducer,
  saga,
  dependencies:[
    AuthModule
  ]
}



// WEBPACK FOOTER //
// ./src/redux/modules/auth/messenger.js